// GET /me: which Xpedition2 person the Keycloak sign-in is linked to. Shared by the avatar and the
// LinkedAccountGate, so it is requested once.
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { config } from '@/config';

export interface Me {
  personGuid: string;
  email?: string;
}

/** A failed GET /me. 404 with code 1040: the sign-in matches no Xpedition2 person; `details` says why. */
export class MeError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
    readonly details: string[] = [],
  ) {
    super(`GET /me: ${status}${code ? ` (${code})` : ''}`);
  }

  get notLinked() {
    return this.status === 404;
  }
}

export function useMe() {
  return useQuery<Me, MeError>({
    queryKey: ['me'],
    enabled: !config.authDisabled,
    staleTime: Infinity,
    // "Not linked" won't change by asking again; other failures (e.g. a token not yet ready) may.
    retry: (count, error) => !error.notLinked && count < 2,
    queryFn: async () => {
      const response = await apiFetch('/me');
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { message?: string; details?: string[] };
        throw new MeError(response.status, body.message, body.details ?? []);
      }
      return (await response.json()) as Me;
    },
  });
}
